import { digitsOnly, normalizeUkDigits, phonesEquivalent } from './phone';

export { digitsOnly, normalizeUkDigits } from './phone';

export type CallDirection = 'incoming' | 'outgoing' | 'unknown';
export type HuntGroupId = 'hq' | 'glazing' | 'unknown';
export type DatePreset = 'today' | 'last_7' | 'last_30' | 'custom';

export type Party = {
  name: string | null;
  number: string | null;
  numberOriginal: string | null;
};

export type NormalizedRecording = {
  recordingId: string;
  callId: string | null;
  ownerUserId: string;
  ownerDisplayName: string;
  startedAt: string | null;
  durationSeconds: number | null;
  direction: CallDirection;
  from: Party;
  to: Party;
  huntGroup: HuntGroupId;
  huntGroupLabel: string;
  huntGroupSource: 'explicit_field' | 'unavailable';
};

export type RecordingUser = {
  userId: string;
  displayName: string;
};

/** Known hunt groups. Used only when an explicit group/resource field is present. */
export const KNOWN_HUNT_GROUPS: Array<{ id: Exclude<HuntGroupId, 'unknown'>; label: string; digits: string[] }> = [
  { id: 'hq', label: 'HQ', digits: ['441243302840', '01243302840'] },
  { id: 'glazing', label: 'Glazing', digits: ['441243302841', '01243302841'] },
];

const EXPLICIT_HUNT_KEYS = [
  'huntGroupNumber',
  'huntGroupName',
  'huntGroupExtension',
  'resourceNumber',
  'resourceName',
  'resourceExtension',
  'groupNumber',
  'groupName',
  'groupExtension',
];

/** External party from the verified incoming/outgoing mapping. Elevate `caller` is remote both ways. */
export function externalParty(row: NormalizedRecording): Party {
  if (row.direction === 'outgoing') return row.to;
  return row.from;
}

export function isUsableExternalNumber(party: Party): boolean {
  const original = String(party.numberOriginal || party.number || '').trim();
  if (!original) return false;
  const lower = original.toLowerCase();
  if (['withheld', 'private', 'anonymous', 'restricted', 'unknown', 'unavailable'].some((word) => lower.includes(word))) {
    return false;
  }
  const digits = party.number || normalizeUkDigits(original);
  return Boolean(digits && digits.length >= 7);
}

export function isOfficeStaffProfile(group?: string | null, role?: string | null): boolean {
  return group === 'staff' && !['fitter', 'labourer', 'supplier', 'document'].includes(String(role || ''));
}

export function isPlausibleUnifiedUserId(value: unknown): value is string {
  const text = String(value || '').trim();
  if (!text || text.length < 8) return false;
  if (/^\d{1,6}$/.test(text)) return false;
  return true;
}

export function recordingUserIdsFromContact(contact: Record<string, unknown>): string[] {
  const keys = ['unifiedUserId', 'unified_user_id', 'unifiedUserID', 'userId', 'user_id'];
  const found: string[] = [];
  keys.forEach((key) => {
    if (isPlausibleUnifiedUserId(contact[key])) found.push(String(contact[key]).trim());
  });
  const type = String(contact.contactType || contact.type || contact.kind || '').toLowerCase();
  const looksExternal = type.includes('external') || type.includes('private') || type.includes('hunt') || type.includes('group') || type.includes('chatbot');
  const looksUser = !type || type === 'user' || type.endsWith('.user');
  if (looksUser && !looksExternal && isPlausibleUnifiedUserId(contact.id)) {
    found.push(String(contact.id).trim());
  }
  return [...new Set(found)];
}

export function contactDisplayName(contact: Record<string, unknown>): string {
  const first = String(contact.firstName || contact.first_name || '').trim();
  const last = String(contact.lastName || contact.last_name || '').trim();
  const combined = `${first} ${last}`.trim();
  return combined
    || String(contact.displayName || contact.display_name || contact.name || contact.fullName || '').trim()
    || String(contact.email || contact.emailAddress || '').trim()
    || 'Unknown user';
}

export function listFromResponse(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data.filter((row) => row && typeof row === 'object') as Record<string, unknown>[];
  if (!data || typeof data !== 'object') return [];
  const record = data as Record<string, unknown>;
  const nested = record.items || record.results || record.contacts || record.recordings || record.callRecordings || record.data || record.records;
  return Array.isArray(nested) ? nested.filter((row) => row && typeof row === 'object') as Record<string, unknown>[] : [];
}

export function mapDirection(value: unknown): CallDirection {
  const text = String(value || '').trim().toLowerCase();
  if (['in', 'inbound', 'incoming', 'incomingcall'].includes(text)) return 'incoming';
  if (['out', 'outbound', 'outgoing', 'outgoingcall'].includes(text)) return 'outgoing';
  return 'unknown';
}

function readString(record: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return null;
}

function readParty(record: Record<string, unknown>, prefixes: string[]): Party {
  const name = readString(record, prefixes.flatMap((prefix) => [
    `${prefix}Name`,
    `${prefix}DisplayName`,
    `${prefix}_name`,
  ]));
  const numberOriginal = readString(record, prefixes.flatMap((prefix) => [
    `${prefix}Number`,
    `${prefix}PhoneNumber`,
    `${prefix}Phone`,
    prefix,
  ]));
  if (numberOriginal && /[A-Za-z]{3,}/.test(numberOriginal) && !/\d{7,}/.test(numberOriginal)) {
    return { name: numberOriginal, number: null, numberOriginal: null };
  }
  return {
    name,
    number: normalizeUkDigits(numberOriginal),
    numberOriginal,
  };
}

function partyFromObject(value: unknown): Party {
  if (!value || typeof value !== 'object') return { name: null, number: null, numberOriginal: null };
  const record = value as Record<string, unknown>;
  const numberOriginal = readString(record, ['number', 'phoneNumber', 'phone', 'e164']);
  return {
    name: readString(record, ['name', 'displayName', 'fullName']),
    number: normalizeUkDigits(numberOriginal),
    numberOriginal,
  };
}

export function extractStartedAt(record: Record<string, unknown>): string | null {
  const raw = readString(record, ['whenCreated', 'startTime', 'startedAt', 'started_at', 'createdTime', 'created_at', 'createdAt', 'timestamp']);
  if (!raw) return null;
  const parsed = Date.parse(raw);
  if (Number.isNaN(parsed)) return null;
  return new Date(parsed).toISOString();
}

export function extractDurationSeconds(record: Record<string, unknown>): number | null {
  const raw = record.duration ?? record.durationSeconds ?? record.durationInSeconds ?? record.length;
  const value = typeof raw === 'string' ? Number(raw) : typeof raw === 'number' ? raw : NaN;
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value);
}

export function extractRecordingId(record: Record<string, unknown>): string | null {
  const raw = readString(record, ['id', 'callRecordingId', 'callRecId', 'recordingId', 'recording_id']);
  return raw;
}

export function extractCallId(record: Record<string, unknown>): string | null {
  return readString(record, ['callId', 'call_id', 'conversationId']);
}

function emptyParty(): Party {
  return { name: null, number: null, numberOriginal: null };
}

function huntFromExplicitFields(record: Record<string, unknown>): { id: HuntGroupId; source: 'explicit_field' | 'unavailable' } {
  for (const key of EXPLICIT_HUNT_KEYS) {
    const raw = record[key];
    if (raw == null || raw === '') continue;
    const label = String(raw).trim();
    const digits = normalizeUkDigits(label);
    const match = KNOWN_HUNT_GROUPS.find((group) => {
      if (digits && group.digits.includes(digits)) return true;
      return group.label.toLowerCase() === label.toLowerCase();
    });
    return { id: match?.id || 'unknown', source: 'explicit_field' };
  }
  return { id: 'unknown', source: 'unavailable' };
}

export function normalizeRecording(
  record: Record<string, unknown>,
  owner: RecordingUser,
): NormalizedRecording | null {
  const recordingId = extractRecordingId(record);
  if (!recordingId) return null;

  const nestedFrom = partyFromObject(record.from);
  const nestedTo = partyFromObject(record.to);
  const callerFlat = readParty(record, ['caller', 'originator']);
  const calleeFlat = readParty(record, ['callee', 'destination', 'called']);
  const callerObj = partyFromObject(record.caller);
  const calleeObj = partyFromObject(record.callee);
  const direction = mapDirection(record.direction || record.callDirection || record.type);

  // Live Elevate payloads expose `caller` as the remote party for both directions.
  const remote = callerObj.number || callerObj.name ? callerObj : (callerFlat.number || callerFlat.name ? callerFlat : emptyParty());
  const local = calleeObj.number || calleeObj.name ? calleeObj : (calleeFlat.number || calleeFlat.name ? calleeFlat : emptyParty());
  const fromExplicit = nestedFrom.number || nestedFrom.name ? nestedFrom : emptyParty();
  const toExplicit = nestedTo.number || nestedTo.name ? nestedTo : emptyParty();

  let from = fromExplicit.number || fromExplicit.name ? fromExplicit : emptyParty();
  let to = toExplicit.number || toExplicit.name ? toExplicit : emptyParty();
  if (!from.number && !from.name && !to.number && !to.name) {
    if (direction === 'outgoing') {
      to = remote;
      from = local;
    } else {
      from = remote;
      to = local;
    }
  }

  const hunt = huntFromExplicitFields(record);
  const huntLabel = hunt.id === 'unknown' ? 'Unknown' : (KNOWN_HUNT_GROUPS.find((group) => group.id === hunt.id)?.label || 'Unknown');

  return {
    recordingId,
    callId: extractCallId(record),
    ownerUserId: owner.userId,
    ownerDisplayName: owner.displayName,
    startedAt: extractStartedAt(record),
    durationSeconds: extractDurationSeconds(record),
    direction,
    from,
    to,
    huntGroup: hunt.id,
    huntGroupLabel: huntLabel,
    huntGroupSource: hunt.source,
  };
}

export function recordingDedupeKey(ownerUserId: string, recordingId: string): string {
  return `${ownerUserId}::${recordingId}`;
}

export function persistedRecordingToNormalized(row: Record<string, unknown>): NormalizedRecording | null {
  const owner: RecordingUser = {
    userId: String(row.owner_user_id || ''),
    displayName: String(row.owner_display_name || 'Unknown user'),
  };
  const raw = row.raw && typeof row.raw === 'object' && !Array.isArray(row.raw)
    ? row.raw as Record<string, unknown>
    : null;
  if (raw && Object.keys(raw).length > 0) {
    const fromRaw = normalizeRecording(raw, owner);
    if (fromRaw) return fromRaw;
  }

  const recordingId = String(row.recording_id || '');
  if (!recordingId || !owner.userId) return null;
  const huntId = String(row.hunt_group || 'unknown') as HuntGroupId;
  return {
    recordingId,
    callId: row.call_id ? String(row.call_id) : null,
    ownerUserId: owner.userId,
    ownerDisplayName: owner.displayName,
    startedAt: row.started_at ? String(row.started_at) : null,
    durationSeconds: Number.isFinite(Number(row.duration_seconds)) ? Number(row.duration_seconds) : null,
    direction: mapDirection(row.direction),
    from: {
      name: row.from_name ? String(row.from_name) : null,
      number: row.from_number ? String(row.from_number) : null,
      numberOriginal: row.from_number_original ? String(row.from_number_original) : null,
    },
    to: {
      name: row.to_name ? String(row.to_name) : null,
      number: row.to_number ? String(row.to_number) : null,
      numberOriginal: row.to_number_original ? String(row.to_number_original) : null,
    },
    huntGroup: huntId === 'hq' || huntId === 'glazing' ? huntId : 'unknown',
    huntGroupLabel: huntId === 'hq' ? 'HQ' : huntId === 'glazing' ? 'Glazing' : 'Unknown',
    huntGroupSource: String(row.hunt_group_source || 'unavailable') === 'explicit_field' ? 'explicit_field' : 'unavailable',
  };
}

export function recordingMatchesPhones(
  row: NormalizedRecording,
  phones: Array<string | null | undefined>,
): boolean {
  const needles = phones.map((value) => normalizeUkDigits(value)).filter((value): value is string => Boolean(value));
  if (!needles.length) return false;
  const candidates = [
    row.from.number,
    row.from.numberOriginal,
    row.to.number,
    row.to.numberOriginal,
  ];
  return candidates.some((candidate) => needles.some((needle) => phonesEquivalent(candidate, needle)));
}

export function upsertByDedupeKey<T extends { ownerUserId: string; recordingId: string }>(rows: T[]): T[] {
  const map = new Map<string, T>();
  rows.forEach((row) => {
    map.set(recordingDedupeKey(row.ownerUserId, row.recordingId), row);
  });
  return [...map.values()];
}

export function londonParts(iso: string, timeZone = 'Europe/London'): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(iso));
  const read = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  return { year: read('year'), month: read('month'), day: read('day') };
}

export function londonDayKey(iso: string): string {
  const { year, month, day } = londonParts(iso);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function formatLondonDateTime(iso?: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

export function formatDuration(seconds?: number | null): string {
  if (seconds == null || !Number.isFinite(seconds)) return '—';
  const total = Math.max(0, Math.round(seconds));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

export function shiftDayKey(dayKey: string, days: number): string {
  const [year, month, day] = dayKey.split('-').map(Number);
  const next = new Date(Date.UTC(year, month - 1, day + days));
  return `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, '0')}-${String(next.getUTCDate()).padStart(2, '0')}`;
}

export function presetRange(preset: DatePreset, nowIso: string, customFrom?: string | null, customTo?: string | null): { fromDay: string; toDay: string } {
  const todayKey = londonDayKey(nowIso);
  if (preset === 'custom' && customFrom && customTo) {
    return { fromDay: customFrom, toDay: customTo };
  }
  if (preset === 'today') return { fromDay: todayKey, toDay: todayKey };
  if (preset === 'last_7') return { fromDay: shiftDayKey(todayKey, -6), toDay: todayKey };
  return { fromDay: shiftDayKey(todayKey, -29), toDay: todayKey };
}

export function inRange(startedAt: string | null, fromDay: string, toDay: string): boolean {
  if (!startedAt) return false;
  const key = londonDayKey(startedAt);
  return key >= fromDay && key <= toDay;
}

export function matchesSearch(row: NormalizedRecording, query: string, extraText = ''): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const hay = [
    row.from.name,
    row.from.number,
    row.from.numberOriginal,
    row.to.name,
    row.to.number,
    row.to.numberOriginal,
    row.ownerDisplayName,
    extraText,
  ].filter(Boolean).join(' ').toLowerCase();
  const compact = hay.replace(/\D/g, '');
  const needleDigits = needle.replace(/\D/g, '');
  return hay.includes(needle) || (!!needleDigits && compact.includes(needleDigits));
}

export function filterRecordings(
  rows: NormalizedRecording[],
  input: {
    fromDay: string;
    toDay: string;
    query?: string;
    direction?: CallDirection | 'all';
    ownerUserId?: string | 'all';
    huntGroup?: HuntGroupId | 'all';
    extraSearchText?: (row: NormalizedRecording) => string;
  },
): NormalizedRecording[] {
  return rows
    .filter((row) => inRange(row.startedAt, input.fromDay, input.toDay))
    .filter((row) => matchesSearch(row, input.query || '', input.extraSearchText?.(row) || ''))
    .filter((row) => !input.direction || input.direction === 'all' || row.direction === input.direction)
    .filter((row) => !input.ownerUserId || input.ownerUserId === 'all' || row.ownerUserId === input.ownerUserId)
    .filter((row) => !input.huntGroup || input.huntGroup === 'all' || row.huntGroup === input.huntGroup)
    .sort((a, b) => Date.parse(b.startedAt || '') - Date.parse(a.startedAt || ''));
}

export function paginate<T>(rows: T[], page: number, pageSize: number): { items: T[]; total: number; page: number; pageSize: number } {
  const size = Math.min(50, Math.max(1, pageSize || 20));
  const total = rows.length;
  const lastPage = Math.max(1, Math.ceil(total / size));
  const current = Math.min(Math.max(1, page || 1), lastPage);
  const start = (current - 1) * size;
  return { items: rows.slice(start, start + size), total, page: current, pageSize: size };
}
